<script setup lang="ts">
const emit = defineEmits(["close", "updateCollectible"]);
const props = defineProps({
  success: {
    type: Boolean,
    required: true,
  },
  currentRoute: {
    type: String,
    required: true,
  },
  collectibleId: {
    type: String,
    required: false,
    default: "",
  },
  minting: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasUpdated: {
    type: Boolean,
    required: false,
    default: false,
  },
  isLocked: {
    type: Boolean,
    required: false,
    default: false,
  },
  isProcessing: {
    type: Boolean,
    required: false,
    default: false,
  },
  isInsufficientPrice: {
    type: Boolean,
    required: false,
    default: false,
  },
  isMismatch: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { t } = useI18n();
const localePath = useLocalePath();

const modalText = computed(() => {
  if (props.success) {
    if (props.currentRoute === "store") {
      return {
        title: t("main.modalBuySuccess.purchaseSuccessful"),
        message: t("main.modalBuySuccess.purchaseSuccessMessage"),
        buttonText: t("main.modalBuySuccess.closeButton"),
      };
    } else {
      return {
        title: props.minting
          ? t("main.modalBuySuccess.mintingSuccessful")
          : t("main.modalBuySuccess.purchaseSuccessful"),
        message: t("main.modalBuySuccess.collectiblePurchaseSuccessMessage"),
        buttonText: t("main.modalBuySuccess.goToCollectible"),
      };
    }
  } else if (props.minting) {
    if (props.isProcessing) {
      return {
        title: t("main.modalBuySuccess.inProgress"),
        message: t("main.modalBuySuccess.processing"),
        buttonText: t("main.modalBuySuccess.goToActivity"),
      };
    } else {
      return {
        title: t("main.modalBuySuccess.mintingFailed"),
        message: t("main.modalBuySuccess.mintingFailedMessage"),
        buttonText: t("main.modalBuySuccess.closeButton"),
      };
    }
  } else if (props.hasUpdated) {
    return {
      title: t("main.modalBuySuccess.transactionFailed"),
      message: t("main.modalBuySuccess.collectibleUpdated"),
      buttonText: t("main.modalBuySuccess.refresh"),
    };
  } else if (props.isLocked) {
    return {
      title: t("main.modalBuySuccess.missedOut"),
      message: t("main.modalBuySuccess.alreadySold"),
      buttonText: t("main.modalBuySuccess.backToItem"),
    };
  } else if (props.isProcessing) {
    return {
      title: t("main.modalBuySuccess.inProgress"),
      message: t("main.modalBuySuccess.processing"),
      buttonText: t("main.modalBuySuccess.goToActivity"),
    };
  } else if (props.isInsufficientPrice) {
    return {
      title: t("main.modalBuySuccess.transactionFailed"),
      message: t("main.modalBuySuccess.transactionFailedMinimumPriceMessage"),
      buttonText: t("main.modalBuySuccess.closeButton"),
    };
  } else if (props.isMismatch) {
    return {
      title: t("main.modalBuySuccess.transactionFailed"),
      message: t("errorMessages.offerAcceptedMismatch"),
      buttonText: t("main.modalBuySuccess.closeButton"),
    };
  } else {
    return {
      title: t("main.modalBuySuccess.transactionFailed"),
      message: t("main.modalBuySuccess.transactionFailedMessage"),
      buttonText: t("main.modalBuySuccess.closeButton"),
    };
  }
});

const handleClick = () => {
  if (props.success) {
    if (props.collectibleId) {
      let path = localePath(`/user-portal/collection/${props.collectibleId}`);
      if (props.minting) {
        path += "?minted=true";
      }
      navigateTo(path);
    } else {
      emit("close");
    }
  } else if (props.hasUpdated) {
    emit("updateCollectible");
  } else if (props.isLocked) {
    emit("close");
  } else if (props.isProcessing) {
    navigateTo(localePath("/user-portal/activity"));
  } else {
    emit("close");
  }
};
</script>
<template>
  <div>
    <div class="flex flex-col items-center gap-2">
      <div class="mt-6 mb-4">
        <NuxtIcon
          v-if="success"
          name="mdi:check-circle-outline"
          class="w-24 h-24 text-success-300" />
        <BaseIconPending v-else-if="isProcessing" class="mx-auto w-36" />
        <NuxtIcon
          v-else
          name="mdi:cancel-circle-outline"
          class="w-24 h-24 text-error-300" />
      </div>
      <div>
        <p class="font-bold text-center text-neutral-200">
          {{ modalText.title }}
        </p>
        <p class="mt-2 text-neutral-300 text-center">
          {{ modalText.message }}
        </p>
      </div>
    </div>
    <div class="mt-5 mb-2 sm:mt-6 flex">
      <div class="w-full mt-auto flex justify-center items-center gap-4">
        <BaseButtonSubmit
          :text="modalText.buttonText"
          data-test="button-close-modal-buy-success"
          size="long"
          :contextual-styles="true"
          @click="handleClick" />
      </div>
    </div>
  </div>
</template>
